/* ============================================
   PostPreview — Realistic platform post preview
   Shows mock of how post looks on each platform.
   ============================================ */
import type { SocialChannel } from '@/lib/types'

interface PostPreviewProps {
  caption: string
  mediaUrls: string[]
  channel: SocialChannel
  hashtags: string[]
}

const BRAND_NAME = '1Conecta'
const BRAND_HANDLE: Record<SocialChannel, string> = {
  instagram: '@1conecta',
  facebook: '1Conecta',
  twitter: '@1conecta',
  pinterest: '1Conecta',
}

function MediaPreview({ urls }: { urls: string[] }) {
  if (urls.length === 0) {
    return (
      <div className="sm-preview__media-placeholder">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
        </svg>
        <span>Sin media adjunta</span>
      </div>
    )
  }

  return (
    <div className="sm-preview__media">
      <img src={urls[0]} alt="Post media" />
      {urls.length > 1 && (
        <span className="sm-preview__media-count">+{urls.length - 1}</span>
      )}
    </div>
  )
}

function formatCaption(caption: string, hashtags: string[]): string {
  const tagStr = hashtags.length > 0 ? '\n\n' + hashtags.map(t => (t.startsWith('#') ? t : `#${t}`)).join(' ') : ''
  return caption + tagStr
}

/* ─── Instagram Preview ─── */
function InstagramPreview({ caption, mediaUrls, hashtags }: Omit<PostPreviewProps, 'channel'>) {
  return (
    <div className="sm-preview sm-preview--instagram">
      {/* Header */}
      <div className="sm-preview__header">
        <div className="sm-preview__avatar" style={{ background: 'linear-gradient(135deg, #E4405F, #F77737)' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>1C</span>
        </div>
        <div className="sm-preview__user">
          <span className="sm-preview__handle">{BRAND_HANDLE.instagram}</span>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 'auto', opacity: 0.5 }}>
          <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
        </svg>
      </div>
      {/* Media */}
      <MediaPreview urls={mediaUrls} />
      {/* Actions */}
      <div className="sm-preview__actions">
        <div style={{ display: 'flex', gap: 14 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </div>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
      </div>
      {/* Caption */}
      <div className="sm-preview__caption">
        <span className="sm-preview__caption-handle">{BRAND_HANDLE.instagram}</span>{' '}
        <span className="sm-preview__caption-text">{formatCaption(caption, hashtags)}</span>
      </div>
    </div>
  )
}

/* ─── Twitter/X Preview ─── */
function TwitterPreview({ caption, mediaUrls, hashtags }: Omit<PostPreviewProps, 'channel'>) {
  return (
    <div className="sm-preview sm-preview--twitter">
      <div style={{ display: 'flex', gap: 10 }}>
        <div className="sm-preview__avatar" style={{ background: '#1C1F2E' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>1C</span>
        </div>
        <div style={{ flex: 1 }}>
          <div className="sm-preview__user">
            <span className="sm-preview__handle" style={{ fontWeight: 700 }}>{BRAND_NAME}</span>
            <span className="sm-preview__subhandle">{BRAND_HANDLE.twitter} · ahora</span>
          </div>
          <div className="sm-preview__caption" style={{ marginTop: 4, padding: 0 }}>
            <span className="sm-preview__caption-text">{formatCaption(caption, hashtags)}</span>
          </div>
          {mediaUrls.length > 0 && (
            <div className="sm-preview__media" style={{ borderRadius: 12, marginTop: 10 }}>
              <img src={mediaUrls[0]} alt="Post media" />
            </div>
          )}
          {/* Tweet actions */}
          <div className="sm-preview__actions" style={{ padding: '8px 0', justifyContent: 'space-between', maxWidth: 280 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Facebook Preview ─── */
function FacebookPreview({ caption, mediaUrls, hashtags }: Omit<PostPreviewProps, 'channel'>) {
  return (
    <div className="sm-preview sm-preview--facebook">
      <div className="sm-preview__header">
        <div className="sm-preview__avatar" style={{ background: '#1877F2' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>1C</span>
        </div>
        <div className="sm-preview__user">
          <span className="sm-preview__handle">{BRAND_NAME}</span>
          <span className="sm-preview__subhandle">Ahora mismo · 🌐</span>
        </div>
      </div>
      <div className="sm-preview__caption" style={{ padding: '8px 12px' }}>
        <span className="sm-preview__caption-text">{formatCaption(caption, hashtags)}</span>
      </div>
      <MediaPreview urls={mediaUrls} />
      <div className="sm-preview__actions" style={{ borderTop: '1px solid var(--border-color)', padding: '8px 12px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
          Me gusta
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          Comentar
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
          Compartir
        </span>
      </div>
    </div>
  )
}

/* ─── Pinterest Preview ─── */
function PinterestPreview({ caption, mediaUrls, hashtags }: Omit<PostPreviewProps, 'channel'>) {
  return (
    <div className="sm-preview sm-preview--pinterest">
      <MediaPreview urls={mediaUrls} />
      <div style={{ padding: '10px 12px' }}>
        <div className="sm-preview__caption" style={{ padding: 0 }}>
          <span className="sm-preview__caption-text" style={{ fontWeight: 600 }}>{formatCaption(caption, hashtags)}</span>
        </div>
        <div className="sm-preview__header" style={{ marginTop: 10, padding: 0 }}>
          <div className="sm-preview__avatar" style={{ background: '#E60023', width: 28, height: 28 }}>
            <span style={{ fontSize: 8, fontWeight: 700, color: '#fff' }}>1C</span>
          </div>
          <span className="sm-preview__handle" style={{ fontSize: 12 }}>{BRAND_HANDLE.pinterest}</span>
        </div>
      </div>
    </div>
  )
}

export default function PostPreview({ caption, mediaUrls, channel, hashtags }: PostPreviewProps) {
  const previewMap: Record<SocialChannel, React.ReactNode> = {
    instagram: <InstagramPreview caption={caption} mediaUrls={mediaUrls} hashtags={hashtags} />,
    twitter: <TwitterPreview caption={caption} mediaUrls={mediaUrls} hashtags={hashtags} />,
    facebook: <FacebookPreview caption={caption} mediaUrls={mediaUrls} hashtags={hashtags} />,
    pinterest: <PinterestPreview caption={caption} mediaUrls={mediaUrls} hashtags={hashtags} />,
  }

  return (
    <div className="sm-preview-wrapper">
      <div className="sm-preview__platform-label">
        Vista previa — {channel.charAt(0).toUpperCase() + channel.slice(1)}
      </div>
      {previewMap[channel]}
    </div>
  )
}
